<template>
  <q-card-section>
    <div
      v-if="$q.screen.gt.xs"
      class="q-gutter-xs"
      ref="test_dom_to_png"
    >
      <q-markup-table
        separator="cell"
        class="text-center equal-columns text-caption"
        wrap-cells
        ref="table1_gt_sm"
      >
        <thead :class="`${isDarkModeGlobal ? 'bg-yellow-9' : 'bg-yellow-2'} ${isDarkModeGlobal ? 'text-white' : 'text-black'}`">
          <tr :class="isDarkModeGlobal ? 'text-white' : 'text-black'">
            <td
              colspan="9"
            >
              Free-Field Values at the Focus
              <q-tooltip v-if="isTooltipEnabled">
                Free-field values (do not account for attenuation due to skull and brain tissue)
              </q-tooltip>
            </td>
          </tr>
          <tr>
            <td>
              Pressure Amplitude
              <br>
              [MPa]
              <br>
              <ppp-pnp-docs />

            </td>
            <td>
              Peak Negative Pressure
              <br>
              [MPa]
              <br>
              <ppp-pnp-docs />
            </td>
            <td>
              Isppa
              <br>
              [W/cm2]
              <br>
              <q-btn
                icon="help"
                color="primary"
                size="sm"
                dense
                flat
              >
                <q-tooltip>
                  Show note
                </q-tooltip>
                <q-popup-proxy>
                  <q-banner :dark="isDarkModeGlobal">
                    <template #avatar>
                      <q-icon
                        name="help"
                        color="primary"
                      />
                    </template>
                    {{ isppaMessage }}
                  </q-banner>
                </q-popup-proxy>
              </q-btn>
            </td>
            <td>
              Non-derated MI
              <br>
              <q-btn
                icon="help"
                color="primary"
                size="sm"
                dense
                flat
              >
                <q-tooltip>
                  Show note
                </q-tooltip>
                <q-popup-proxy>
                  <q-banner :dark="isDarkModeGlobal">
                    <template #avatar>
                      <q-icon
                        name="help"
                        color="primary"
                      />
                    </template>
                    The mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa. This estimate of MI is based on non-derated pressure and does not correspond to the formal definition of MI provided in the <a href='https://doi.org/10.1016/j.brs.2024.04.013' target='_blank'><strong>ITRUSST Standardised Reporting Consensus</strong></a>
                  </q-banner>
                </q-popup-proxy>
              </q-btn>
            </td>
            <td v-if="calculatorStore.pulseAndTrainOnly || calculatorStore.allTimeTogglesOn">
              Ispta of pulse train<br>[W/cm2]
            </td>
            <td v-if="calculatorStore.allTimeTogglesOn">
              Ispta of pulse train repetition<br>[W/cm2]
            </td>
            <td>Energy deposition<br>[J/cm2]</td>
            <td>Rate of energy deposition<br>[J/(cm2 * s)]</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {{ format(ppp_mpa) }}
            </td>
            <td>
              {{ format(pnp_mpa) }}
            </td>
            <td>
              <q-spinner-gears
                v-if="isppaLoading.pulse"
                color="primary"
                size="2em"
              />
              {{ format(isppa) }}
            </td>
            <td>
              {{ non_derated_MI.toFixed(4) }}
            </td>
            <td v-if="calculatorStore.pulseAndTrainOnly || calculatorStore.allTimeTogglesOn">
              <q-spinner-gears
                v-if="isppaLoading.train"
                color="primary"
                size="2em"
              />
              {{ format(calculatorStore.ispta_pulse_train) }}
            </td>
            <td v-if="calculatorStore.allTimeTogglesOn">
              <q-spinner-gears
                v-if="isppaLoading.repetition"
                color="primary"
                size="2em"
              />
              {{ format(calculatorStore.ispta_pulse_train_repetition) }}
            </td>
            <td>
              {{ calculatorStore.energy_deposition }}
            </td>
            <td>
              {{ calculatorStore.rate_of_energy_deposition }}
            </td>
          </tr>
        </tbody>
      </q-markup-table>
      <q-markup-table
        v-if="derate"
        separator="cell"
        class="text-center equal-columns text-caption"
        wrap-cells
        ref="table2_gt_sm"
      >
        <thead
          :class="`${isDarkModeGlobal ? 'bg-teal-7' : 'bg-teal-4'} ${isDarkModeGlobal ? 'text-white' : 'text-white'}`"
        >
          <tr>
            <td
              colspan="9"
            >
              In Situ Estimates at the Focus with a Conservative Derating
              <q-tooltip v-if="isTooltipEnabled">
                Values accounting for attenuation due to skull and brain tissue
              </q-tooltip>
            </td>
          </tr>
          <tr>
            <td>
              Pressure Amplitude
              <br>
              [MPa]
              <br>
              <ppp-pnp-docs icon-color="white"/>
            </td>
            <td>
              Peak Negative Pressure
              <br>
              [MPa]
              <br>
              <ppp-pnp-docs icon-color="white"/>
            </td>
            <td>
              Isppa
              <br>
              [W/cm2]
              <br>
              <q-btn
                icon="help"
                color="white"
                size="sm"
                dense
                flat
              >
                <q-tooltip>
                  Show note
                </q-tooltip>
                <q-popup-proxy>
                  <q-banner :dark="isDarkModeGlobal">
                    <template #avatar>
                      <q-icon
                        name="help"
                        color="primary"
                      />
                    </template>
                    The mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa. This estimate of MI is based on non-derated pressure and does not correspond to the formal definition of MI provided in the <a href="https://doi.org/10.1016/j.brs.2024.04.013" target="_blank"><strong>ITRUSST Standardised Reporting Consensus</strong></a>.
                  </q-banner>
                </q-popup-proxy>
              </q-btn>
            </td>
            <td>
              MI
              <br>
              derated by 0.3 dB/cm/MHz
              <br>
              <q-btn
                icon="help"
                color="white"
                size="sm"
                dense
                flat
              >
                <q-tooltip>
                  Show note
                </q-tooltip>
                <q-popup-proxy>
                  <q-banner :dark="isDarkModeGlobal">
                    <template #avatar>
                      <q-icon
                        name="help"
                        color="primary"
                      />
                    </template>
                    <span v-html="miMessage" />
                  </q-banner>
                </q-popup-proxy>
              </q-btn>
            </td>
            <td>
              MI<sub>tc</sub><br></br>
              <q-btn
                icon="help"
                color="white"
                size="sm"
                dense
                flat
              >
                <q-tooltip>
                  Show note
                </q-tooltip>
                <q-popup-proxy>
                  <q-banner :dark="isDarkModeGlobal">
                    <template #avatar>
                      <q-icon
                        name="help"
                        color="primary"
                      />
                    </template>
                    <span v-html="mi_tc_Message" />
                  </q-banner>
                </q-popup-proxy>
              </q-btn>
            </td>
            <td v-if="calculatorStore.pulseAndTrainOnly || calculatorStore.allTimeTogglesOn">
              Ispta of pulse train
              <br>
              [W/cm2]
            </td>
            <td v-if="calculatorStore.allTimeTogglesOn">
              Ispta of pulse train repetition
              <br>
              [W/cm2]
            </td>
            <td>
              Energy deposition
              <br>
              [J/cm2]
            </td>
            <td>
              Rate of energy deposition
              <br>
              [J/(cm2 * s)]
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {{ format(derated_ppp_mpa) }}
            </td>
            <td>
              {{ format(derated_pnp_mpa) }}
            </td>
            <td>
              <q-spinner-gears
                v-if="isppaLoading.pulse"
                color="primary"
                size="2em"
              />
              {{ format(derated_isppa) }}
            </td>
            <td>
              {{ format(MI) }}
            </td>
            <td>
              {{ format(derated_MI) }}
            </td>
            <td v-if="calculatorStore.pulseAndTrainOnly || calculatorStore.allTimeTogglesOn">
              <q-spinner-gears
                v-if="isppaLoading.train"
                color="primary"
                size="2em"
              />
              {{ format(calculatorStore.derated_ispta_pulse_train) }}
            </td>
            <td v-if="calculatorStore.allTimeTogglesOn">
              <q-spinner-gears
                v-if="isppaLoading.repetition"
                color="primary"
                size="2em"
              />
              {{ format(calculatorStore.derated_ispta_pulse_train_repetition) }}
            </td>
            <td>
              {{ calculatorStore.derated_energy_deposition }}
            </td>
            <td>
              {{ calculatorStore.derated_rate_of_energy_deposition }}
            </td>
          </tr>
        </tbody>
      </q-markup-table>
    </div>
    <div
      v-else
      ref="test_dom_to_png"
    >
      <div class="q-pa-sm">
        <q-toolbar-title class="text-primary">
          Free-Field Values at the Focus
        </q-toolbar-title>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="ppp_mpa"
          readonly
          label="Pressure Amplitude [MPa]"

        >
          <template #append>
            <ppp-pnp-docs />
          </template>
        </q-input>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="pnp_mpa"
          readonly
          label="Peak Negative Pressure [MPa]"
        >
          <template #append>
            <ppp-pnp-docs />
          </template>
        </q-input>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="non_derated_MI"
          readonly
          label="Non-derated MI"
        >
          <template #append>
            <q-btn
                  icon="help"
                  color="primary"
                  size="sm"
                  dense
                  flat
                >
                  <q-tooltip>
                    Show note
                  </q-tooltip>
                  <q-popup-proxy>
                    <q-banner :dark="isDarkModeGlobal">
                      <template #avatar>
                        <q-icon
                          name="help"
                          color="primary"
                        />
                      </template>
                      The mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa. This estimate of MI is based on non-derated pressure and does not correspond to the formal definition of MI provided in the <a href="https://doi.org/10.1016/j.brs.2024.04.013" target="_blank"><strong>ITRUSST Standardised Reporting Consensus</strong></a>.
                    </q-banner>
                  </q-popup-proxy>
                </q-btn>
          </template>
        </q-input>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="format(isppa)"
          readonly
          label="Isppa [W/cm2]"
        >
          <template #append>
            <q-btn
              icon="help"
              color="primary"
              size="sm"
              dense
              flat
            >
              <q-popup-proxy :offset="[0, 20]">
                <q-banner :dark="isDarkModeGlobal">
                  <template #avatar>
                    <q-icon
                      name="help"
                      color="primary"
                    />
                  </template>
                  {{ isppaMessage }}
                </q-banner>
              </q-popup-proxy>
            </q-btn>
          </template>
        </q-input>
        <q-input
          v-if="calculatorStore.pulseAndTrainOnly || calculatorStore.allTimeTogglesOn"
          :dark="isDarkModeGlobal"
          :model-value="format(calculatorStore.ispta_pulse_train)"
          readonly
          label="Ispta of pulse train [W/cm2]"
        />
        <q-input
          v-if="calculatorStore.allTimeTogglesOn"
          :dark="isDarkModeGlobal"
          :model-value="format(calculatorStore.ispta_pulse_train_repetition)"
          readonly
          label="Ispta of pulse train repetition [W/cm2]"
        />
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="calculatorStore.energy_deposition"
          readonly
          label="Energy deposition [J/cm2]"
        />
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="calculatorStore.rate_of_energy_deposition"
          readonly
          label="Rate of energy deposition [J/(cm2 * s)]"
        />
      </div>
      <div
        v-if="derate"
        class="q-pa-sm"
      >
        <q-toolbar-title class="text-primary">
          In Situ Estimates at the Focus with a Conservative Derating
        </q-toolbar-title>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="derated_ppp_mpa"
          readonly
          label="Pressure Amplitude [MPa]"
        >
          <template #append>
            <ppp-pnp-docs />
          </template>
        </q-input>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="derated_pnp_mpa"
          readonly
          label="Peak Negative Pressure [MPa]"
        >
          <template #append>
            <ppp-pnp-docs />
          </template>
        </q-input>
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="format(derated_isppa)"
          readonly
          label="Isppa [W/cm2]"
        >
          <template #append>
            <q-btn
              icon="help"
              color="primary"
              size="sm"
              dense
              flat
            >
              <q-tooltip>
                Show note
              </q-tooltip>
              <q-popup-proxy :offset="[20, 20]">
                <q-banner :dark="isDarkModeGlobal">
                  <template #avatar>
                    <q-icon
                      name="help"
                      color="primary"
                    />
                  </template>
                  {{ isppaMessage }}
                </q-banner>
              </q-popup-proxy>
            </q-btn>
          </template>
        </q-input>

        <q-input
          :dark="isDarkModeGlobal"
          :model-value="MI.toFixed(2)"
          readonly
          label="MI derated by 0.3 dB/cm/MHz"
        >
          <template #append>
            <q-btn
              icon="help"
              color="primary"
              size="sm"
              dense
              flat
            >
              <q-popup-proxy :offset="[0, 20]">
                <q-banner :dark="isDarkModeGlobal">
                  <template #avatar>
                    <q-icon
                      name="help"
                      color="primary"
                    />
                  </template>
                  <span v-html="miMessage" />
                </q-banner>
              </q-popup-proxy>
            </q-btn>
          </template>
        </q-input>


        <q-input
          :dark="isDarkModeGlobal"
          :model-value="+derated_MI.toFixed(2)"
          readonly
          label=""
        >
          <template #label>
            MI<sub>tc</sub>
          </template>
          <template #append>
            <q-btn
              icon="help"
              color="primary"
              size="sm"
              dense
              flat
            >
              <q-popup-proxy :offset="[0, 20]">
                <q-banner :dark="isDarkModeGlobal">
                  <template #avatar>
                    <q-icon
                      name="help"
                      color="primary"
                    />
                  </template>
                  <span v-html="mi_tc_Message" />
                </q-banner>
              </q-popup-proxy>
            </q-btn>
          </template>
        </q-input>
        <q-input
          v-if="calculatorStore.pulseAndTrainOnly || calculatorStore.allTimeTogglesOn"
          :dark="isDarkModeGlobal"
          :model-value="format(calculatorStore.derated_ispta_pulse_train)"
          readonly
          label="Ispta of pulse train [W/cm2]"
        />
        <q-input
          v-if="calculatorStore.allTimeTogglesOn"
          :dark="isDarkModeGlobal"
          :model-value="format(calculatorStore.derated_ispta_pulse_train_repetition)"
          readonly
          label="Ispta of pulse train repetition [W/cm2]"
        />
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="calculatorStore.derated_energy_deposition"
          readonly
          label="Energy deposition [J/cm2]"
        />
        <q-input
          :dark="isDarkModeGlobal"
          :model-value="calculatorStore.derated_rate_of_energy_deposition"
          readonly
          label="Rate of energy deposition [J/(cm2 * s)]"
        />
      </div>
    </div>
  </q-card-section>
</template>
<script setup lang="ts">
import { storeToRefs } from "pinia"
import { useCalculatorStore } from '../stores/calculator.store'
const calculatorStore = useCalculatorStore()
import {useQuasar} from "quasar";
import PppPnpDocs from "./DialogPeakPressureGraphDocs.vue";
import ReferencePopupProxy from "./PopupProxyLiteratureReference.vue";
const {
  isTooltipEnabled,
  ppp_mpa,
  pnp_mpa,
  isppa,
  MI,
  derate,
  derated_isppa,
  derated_MI,
  derated_ppp_mpa,
  derated_pnp_mpa,
  isDarkModeGlobal,
  isppaLoading,
  non_derated_MI
} = storeToRefs(calculatorStore)

const $q = useQuasar()

const isppaMessage = `Spatial-peak pulse average intensity; the Isppa is calculated for an individual pulse taking pulse level ramping into account. However, when ramping is applied at the pulse train or pulse train repetition level, the pulse amplitude and therefore Isspa is not consistent over time. The Isppa reported here is the maximum value which corresponds to the highest amplitude pulses, which occur after the on-ramp of the pulse, pulse train, and/or pulse train repetition level is completed, and before the off-ramp begins.`
const miMessage = 'The mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa. This estimate of MI is based on derated pressure as defined in the <a href="https://doi.org/10.1016/j.brs.2024.04.013" target="_blank"><strong>ITRUSST Standardised Reporting Consensus</strong></a>. The pressure is derated using an attenuation coefficient of 0.3 dB/cm/MHz.'
const mi_tc_Message = 'According to the formal definition, the mechanical index (MI) is estimated using the peak-rarefactional pressure in MPa, derated using an attenuation coefficient of 0.3 dB/cm/MHz. MI<sub>tc</sub>, on the other hand, assumes there is skull in the beam path, and therefore, provides a more realistic, though still conservative estimate. We calculate the derating value for MI<sub>tc</sub> using the function reported by <a href="https://doi.org/10.1016/j.brs.2022.12.005" target="_blank"><strong>Attali et al. 2023</strong></a> (figure 5) which uses fundamental frequency and ultrasound beam diameter as inputs. An additional derating factor is applied in soft tissues; see the "Assumptions" panel → Brain → Attenuation coefficient. Please see the <a href="https://doi.org/10.1016/j.brs.2024.04.013" target="_blank"><strong>ITRUSST Standardised Reporting Consensus</strong></a> for details.'
function format(val: any) {
  if (val) {
    return (typeof val) === 'string'
        ? (+val).toFixed(2)
        : val.toFixed(2)
  }
  return ""
}
</script>
<style>
.equal-columns {
  width: 100%;
  table-layout: fixed;
}

.equal-columns th,
.equal-columns td {
  width: 13.33%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>